import { ArrowRightOutlined } from "@ant-design/icons";
import { Button, Col, Collapse, Image, Row, Typography } from "antd";
import AppImages from "assets/appImages";
import { motion } from "framer-motion";

const { Title, Paragraph } = Typography;

const LandingPage = () => {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="h-[100dvh] overflow-y-auto bg-white px-6 pt-8 lg:px-24"
    >
      <Row gutter={[48, 48]} align="middle" justify="space-between">
        <Col xs={24} md={12}>
          <motion.div
            initial={{ x: -60, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            transition={{ duration: 0.6 }}
          >
            <Image src={AppImages.Logo} alt="eN-LIS Logo" width={50} />
            <Title level={1} style={{ fontSize: "3rem", marginBottom: "1rem" }}>
              Welcome to{" "}
              <motion.span
                style={{
                  color: "#1890ff",
                  display: "inline-block",
                  marginLeft: "0.5rem",
                }}
                animate={{ scale: [1, 1.05, 1], opacity: [1, 0.75, 1] }}
                transition={{
                  repeat: Infinity,
                  duration: 1,
                  ease: "easeInOut",
                }}
              >
                eN-LIS
              </motion.span>
            </Title>
            <Paragraph style={{ fontSize: "1.25rem", color: "#595959" }}>
              Streamline your laboratory operations with our modern Laboratory
              Information System. Reliable. Secure. Scalable.
            </Paragraph>
            <Button
              type="primary"
              size="large"
              icon={<ArrowRightOutlined />}
              style={{ marginTop: "2rem" }}
              href="/signin"
            >
              Get Started
            </Button>
          </motion.div>
        </Col>

        <Col xs={24} md={12}>
          <motion.img
            src={AppImages.illustration}
            alt="Lab Illustration"
            style={{ width: "100%", maxWidth: "400px" }}
            initial={{ x: 60, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            transition={{ duration: 0.6 }}
          />
        </Col>
      </Row>

      <motion.div
        className="mt-24"
        initial={{ opacity: 0, y: 60 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3 }}
      >
        <Title level={2}>Why Choose eN-LIS?</Title>
        <Row gutter={[32, 32]} className="mt-8">
          {[
            {
              title: "Real-time Results",
              desc: "Instant access to test results and analytics.",
            },
            {
              title: "Secure & Compliant",
              desc: "HIPAA/GDPR compliant infrastructure.",
            },
            {
              title: "Customizable Workflows",
              desc: "Tailor the system to your lab’s unique needs.",
            },
          ].map((feature, idx) => (
            <Col xs={24} md={8} key={idx}>
              <motion.div
                whileHover={{ scale: 1.03 }}
                className="p-6 rounded-md shadow-md bg-gray-50 hover:shadow-xl transition-shadow"
              >
                <Title level={4}>{feature.title}</Title>
                <Paragraph>{feature.desc}</Paragraph>
              </motion.div>
            </Col>
          ))}
        </Row>
      </motion.div>

      <motion.section
        className="mt-24 max-w-4xl mx-auto text-center border-b border-gray-300 pb-8"
        initial={{ opacity: 0, y: 60 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.4 }}
      >
        <Title level={2}>About eN-LIS</Title>
        <Paragraph style={{ fontSize: "1rem", color: "#4B5563" }}>
          eN-LIS is a modern Electronic National Laboratory Information System
          built to streamline health data workflows across Nigeria. Designed for
          reliability, compliance, and scalability, eN-LIS empowers labs with
          real-time results, customizable workflows, and secure data
          infrastructure.
        </Paragraph>
      </motion.section>

      {/* FAQ Section */}
      <motion.section
        className="mt-16 max-w-4xl mx-auto text-center"
        initial={{ opacity: 0, y: 60 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.6 }}
      >
        <Title level={2}>Frequently Asked Questions</Title>
        <div className="mt-8 text-left">
          <Collapse accordion bordered defaultActiveKey={undefined}>
            {[
              {
                q: "How do I reset my password?",
                a: "Contact your Facility Admin to initiate a password reset.",
              },
              {
                q: "Who do I contact for support?",
                a: "Contact your Facility Admin or email support@en-lis.com.",
              },
              {
                q: "Can I access eN-LIS on mobile?",
                a: "Yes, eN-LIS is mobile-friendly and works on most devices.",
              },
              {
                q: "How do I print a report or result?",
                a: "Use the print button in the Reports or Results section.",
              },
            ].map((faq, idx) => (
              <Collapse.Panel header={faq.q} key={idx}>
                <Paragraph style={{ color: "#4B5563" }}>{faq.a}</Paragraph>
              </Collapse.Panel>
            ))}
          </Collapse>
        </div>
      </motion.section>

      <motion.div
        className="mt-24 text-center"
        initial={{ opacity: 0, y: 60 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.5 }}
      >
        <footer className="w-full text-center py-8 px-4 bg-gray-50 border-t text-gray-600 text-sm">
          <div className="max-w-7xl mx-auto grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-6 text-left">
            {/* Company Info */}
            <div>
              <h4 className="font-semibold text-gray-800 mb-2">About eN-LIS</h4>
              <p className="text-sm">
                A modern Electronic National Laboratory Information System for
                streamlining health data workflows in Nigeria.
              </p>
            </div>

            {/* Quick Links */}
            <div>
              <h4 className="font-semibold text-gray-800 mb-2">Quick Links</h4>
              <ul className="space-y-1">
                <li>
                  <a href="/signin" className="hover:underline !text-green-700">
                    Launch App
                  </a>
                </li>
                <li>
                  <a href="/docs" className="hover:underline !text-green-700">
                    Documentation
                  </a>
                </li>
                <li>
                  <a href="/terms" className="hover:underline !text-green-700">
                    Terms of Service
                  </a>
                </li>
              </ul>
            </div>

            {/* Partners */}
            <div>
              <h4 className="font-semibold text-gray-800 mb-2">Partners</h4>
              <ul className="space-y-1">
                <li>
                  <a
                    href="https://gghnigeria.org/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="hover:underline !text-green-700"
                  >
                    Georgetown Global Health Nigeria (GGHN)
                  </a>
                </li>
                <li>
                  <a
                    href="https://ncdc.gov.ng/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="hover:underline !text-green-700"
                  >
                    NCDC
                  </a>
                </li>
              </ul>
            </div>

            {/* Contact / Follow */}
            <div>
              <h4 className="font-semibold text-gray-800 mb-2">Contact</h4>
              <ul className="space-y-1">
                <li>
                  <a
                    href="mailto:support@en-lis.com"
                    className="hover:underline !text-green-700"
                  >
                    support@en-lis.com
                  </a>
                </li>
                <li>
                  <a
                    href="https://gitlab.com/en-lis"
                    target="_blank"
                    className="hover:underline !text-green-700"
                  >
                    contribute
                  </a>
                </li>
              </ul>
            </div>
          </div>

          {/* Divider */}
          <div className="border-t mt-8 pt-4">
            <p className="text-xs text-gray-400">
              &copy; {new Date().getFullYear()} eN-LIS. Built by
              <a
                href="https://gghnigeria.org/"
                target="_blank"
                rel="noopener noreferrer"
                className="ml-1 underline !text-green-700"
              >
                Georgetown Global Health Nigeria (GGHN)
              </a>
              . All rights reserved.
            </p>
          </div>
        </footer>
      </motion.div>
    </motion.div>
  );
};

export default LandingPage;
